import { TreeSelect } from "antd";
import type { TreeSelectProps } from "antd/lib/tree-select";
import type { DefaultOptionType } from "rc-tree-select/lib/TreeSelect";
import type { Key } from "react";
import { FC, useMemo } from "react";

import { IInputProps } from "../../../modules/form";
import { ObjectUtils } from "../../../modules/utils";

import "./index.scss";

interface IAsyncTreeDataConfig {
  pathTitle: string | ((item: any) => any);
  pathKey: string | ((item: any) => any);
  response: any;
  pathChildren?: string;
}

interface ITreeFieldNames {
  label?: string;
  value?: string;
  children?: string;
}

export interface IInputTreeProps extends IInputProps {
  treeData?: DefaultOptionType[];
  asyncData?: IAsyncTreeDataConfig;
  fieldNames?: ITreeFieldNames;
  treeProps?: Omit<TreeSelectProps<Key>, "treeData" | "value">;
  allowClear?: boolean;
}

const DEFAULT_FIELD_NAMES: Required<ITreeFieldNames> = {
  label: "title",
  value: "value",
  children: "children",
};

const toKey = (value: any): Key => {
  if (typeof value === "string" || typeof value === "number") return value;
  return String(value ?? "");
};

const ensureArray = (value: any): Key[] => {
  if (Array.isArray(value)) return value.map(toKey);
  if (value === undefined || value === null || value === "") return [];
  return [toKey(value)];
};

const buildTreeData = (
  nodes: any[],
  paths: { title: string | ((item: any) => any); key: string | ((item: any) => any); children: string },
): DefaultOptionType[] => {
  if (!Array.isArray(nodes)) return [];

  return nodes.map((item) => {
    const children = buildTreeData(ObjectUtils.getIn(item, paths.children, []), paths);
    return {
      ...item,
      title: typeof paths.title === "function" ? paths.title(item) : ObjectUtils.getIn(item, paths.title),
      key: typeof paths.key === "function" ? paths.key(item) : ObjectUtils.getIn(item, paths.key),
      value: typeof paths.key === "function" ? paths.key(item) : ObjectUtils.getIn(item, paths.key),
      children: children.length ? children : undefined,
    };
  });
};

export const InputTree: FC<IInputTreeProps> = (props) => {
  const { asyncData, fieldNames, treeData: propTreeData, treeProps, allowClear } = props;

  const asyncResponse = asyncData?.response;
  const asyncErrorMessage = ObjectUtils.getIn(asyncResponse, "Error.Message");

  const treeData = useMemo<DefaultOptionType[]>(() => {
    if (asyncData && asyncResponse && !asyncErrorMessage) {
      return buildTreeData(ObjectUtils.getIn(asyncResponse, "Data", []), {
        title: asyncData.pathTitle,
        key: asyncData.pathKey,
        children: asyncData.pathChildren || "children",
      });
    }

    if (propTreeData) return propTreeData;
    if (props.options) return props.options as DefaultOptionType[];

    return [];
  }, [asyncData, asyncResponse, asyncErrorMessage, propTreeData, props.options]);

  const {
    className: treeClassName,
    onChange: treeOnChange,
    fieldNames: overrideFieldNames,
    showSearch: overrideShowSearch,
    allowClear: overrideAllowClear,
    notFoundContent: overrideNotFoundContent,
    ...restTreeProps
  } = treeProps || {};

  const mergedFieldNames = {
    ...DEFAULT_FIELD_NAMES,
    ...overrideFieldNames,
    ...fieldNames,
  };

  const mergedTreeClassName = ["InputTree", treeClassName].filter(Boolean).join(" ");

  const disabled = !!(props.isDisabled || props.isLoading);
  const multiple = !!props.multiple;
  const normalizedValue = multiple
    ? ensureArray(props.value)
    : props.value === "" || props.value === null || props.value === undefined
    ? undefined
    : props.value;
  const notFoundContent =
    overrideNotFoundContent ||
    (asyncErrorMessage ? `Error: ${asyncErrorMessage}` : props.placeholder || "Không có dữ liệu");
  const resolvedAllowClear =
    typeof allowClear === "boolean" ? allowClear : typeof overrideAllowClear === "boolean" ? overrideAllowClear : true;
  const resolvedShowSearch = typeof overrideShowSearch === "boolean" ? overrideShowSearch : true;

  const handleChange: TreeSelectProps<Key>["onChange"] = (value, labelList, extra) => {
    const nextValue = multiple ? (Array.isArray(value) ? value : ensureArray(value)) : value;
    props.onChange(nextValue);
    props.onSelectedChange?.(extra?.triggerNode);
    props.onTouched(true);
    treeOnChange?.(value, labelList, extra);
  };

  return (
    <TreeSelect
      {...restTreeProps}
      className={mergedTreeClassName}
      treeData={treeData}
      fieldNames={mergedFieldNames}
      value={normalizedValue}
      onChange={handleChange}
      placeholder={props.placeholder || "-- Select --"}
      disabled={disabled}
      treeCheckable={multiple}
      multiple={multiple}
      allowClear={resolvedAllowClear}
      showSearch={resolvedShowSearch}
      notFoundContent={notFoundContent}
      dropdownMatchSelectWidth
    />
  );
};

export default InputTree;
